import React from 'react';
import { i18n } from 'config/locales';

import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import classnames from 'classnames';
import {
  HistoryEmploymentItemModel,
  AdditionalActivityModel,
} from 'forms/sections/history/employment/types';
import { DateModel } from 'forms/shared/types';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import BranchCollection from 'components/Form/BranchCollection';
import Field from 'components/Form/Field';
import DateRange from 'components/Form/DateRange';
import Text from 'components/Form/Text';

type AdditionalActivityType = HistoryEmploymentItemModel['Additional']
interface Props extends ValidationElementProps {
  employmentFromDate: DateModel
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
}

export default class AdditionalActivity extends ValidationElement<Props> {
  updateBranch = (values: Partial<AdditionalActivityType>) => {
    this.props.onUpdate({
      ...values,
    });
  };

  render() {
    return (
      <div className={classnames('activity', this.props.className)}>
        <BranchCollection
          label={i18n.t('history.employment.default.additionalActivity.label')}
          labelSize="h4"
          appendLabel={i18n.t('history.employment.default.additionalActivity.collection.append')}
          appendSize="h4"
          {...(this.props || {})}
          onUpdate={this.updateBranch}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        >
          <AdditionalActivityItem {...this.props} bind name="Item" />
        </BranchCollection>
      </div>
    );
  }
}

interface ItemProps
  extends Props,
    Partial<AdditionalActivityModel>,
    Omit<AccordionItemProps, 'name'> {}

const AdditionalActivityItem: React.FC<ItemProps> = ({
  scrollIntoView,
  required,
  employmentFromDate,
  attribute: propAttribute,
  $index,
  Position,
  Supervisor,
  DatesEmployed,
  onUpdate: propOnUpdate,
  onError,
}) => {
  const attribute = `${propAttribute}.items[${$index}].Item`;
  const dataModel: Partial<AdditionalActivityModel> = {
    Position,
    Supervisor,
    DatesEmployed,
  };
  const onUpdate = (key: keyof AdditionalActivityModel) => (values: unknown) => {
    propOnUpdate({
      ...dataModel,
      [key]: values,
    });
  };

  return (
    <>
      <Field
        attribute={`${attribute}.Position`}
        title={i18n.t('history.employment.default.additionalActivity.position.label')}
        titleSize="h4"
        adjustFor="labels"
        scrollIntoView={scrollIntoView}
      >
        <Text
          name="Position"
          className="text"
          required={required}
          attribute={`${attribute}.Position`}
          {...Position}
          onUpdate={onUpdate('Position')}
          onError={onError}
        />
      </Field>

      <Field
        attribute={`${attribute}.Supervisor`}
        title={i18n.t('history.employment.default.additionalActivity.supervisor.label')}
        titleSize="h4"
        adjustFor="labels"
        scrollIntoView={scrollIntoView}
      >
        <Text
          attribute={`${attribute}.Supervisor`}
          name="Supervisor"
          className="text"
          required={required}
          {...Supervisor}
          onUpdate={onUpdate('Supervisor')}
          onError={onError}
        />
      </Field>

      <Field
        attribute={`${attribute}.DatesEmployed`}
        title={i18n.t('history.employment.default.additionalActivity.heading.datesEmployed')}
        titleSize="h4"
        help="history.employment.default.additionalActivity.datesEmployed.help"
        adjustFor="daterange"
        shrink
        scrollIntoView={scrollIntoView}
      >
        <DateRange
          attribute={`${attribute}.DatesEmployed`}
          name="DatesEmployed"
          minDateEqualTo
          dateRangePrefix="additionalActivity"
          maxDate={employmentFromDate}
          maxDateEqualTo
          allowPresent={false}
          required={required}
          hideFromDay
          hideToDay
          {...DatesEmployed}
          onUpdate={onUpdate('DatesEmployed')}
          onError={onError}
        />
      </Field>
    </>
  );
};

AdditionalActivity.defaultProps = {
  employmentFromDate: {
    month: `${new Date().getMonth() + 1}`,
    day: `${new Date().getDate()}`,
    year: `${new Date().getFullYear()}`,
  },
  onUpdate: (queue) => {},
  onError: (value, arr) => arr,
};
